/**
 * Admin authentication utilities
 * Used by admin pages to get the appropriate authentication token
 */

/**
 * Get the appropriate auth token for admin API calls
 * Checks adminToken first (traditional admin login), then falls back to regular token
 * (for users with admin emails like rauf@iolab.co), then OAuth session token
 * 
 * @returns {string|null} The auth token or null if not authenticated
 */
export const getAdminAuthToken = () => {
  // Try adminToken first (traditional admin login)
  const adminToken = localStorage.getItem('adminToken');
  if (adminToken) return adminToken;
  
  // Fallback to regular token (for admin email users)
  const regularToken = localStorage.getItem('token');
  if (regularToken) return regularToken;
  
  // No JWT token - OAuth users don't have tokens, they use session tokens
  return null;
};

/**
 * Get OAuth session token for admin API calls
 * @returns {string|null} The OAuth session token or null if not available
 */
export const getOAuthSessionToken = () => {
  // Check localStorage for OAuth session token
  const oauthToken = localStorage.getItem('oauth_session_token');
  if (oauthToken) return oauthToken;
  
  // Check cookies for session token
  const sessionCookie = document.cookie.split('; ').find(row => row.startsWith('session_token='));
  if (sessionCookie) {
    return sessionCookie.split('=')[1];
  }
  
  return null;
};

/**
 * Get axios config with admin authorization header
 * Supports both JWT tokens and OAuth session tokens
 * @returns {Object} Axios request config with appropriate auth headers
 */
export const getAdminAuthConfig = () => {
  const token = getAdminAuthToken();
  const oauthSessionToken = getOAuthSessionToken();
  
  const config = {
    headers: {},
    withCredentials: true
  };
  
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  
  if (oauthSessionToken && !token) {
    // OAuth session token - use custom header
    config.headers['X-OAuth-Session-Token'] = oauthSessionToken;
  }
  
  return config;
};

